import pickBy from 'lodash/pickBy';
import sum from 'lodash/sum';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import endOfDay from 'date-fns/endOfDay';
import endOfWeek from 'date-fns/endOfWeek';
import endOfMonth from 'date-fns/endOfMonth';
import endOfYear from 'date-fns/endOfYear';
import startOfDay from 'date-fns/startOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import startOfYear from 'date-fns/startOfYear';

import { DateType } from '../../../../../../../types';

class UniversalFilterDateUtils {
  protected static today(): Date {
    return new Date();
  }

  protected static stringifyGteLte<
    T extends { gte?: Date; lte?: Date },
    S = Record<keyof T, DateType>
  >({ gte, lte }: T): S {
    return pickBy({ gte: gte?.toJSON(), lte: lte?.toJSON() }) as S;
  }

  protected static getDayRange(targetDay: Date): {
    gte: DateType;
    lte: DateType;
  } {
    return UniversalFilterDateUtils.stringifyGteLte({
      gte: startOfDay(targetDay),
      lte: endOfDay(targetDay)
    });
  }

  protected static getBeforeDay(targetDay: Date): {
    lte: DateType;
  } {
    return UniversalFilterDateUtils.stringifyGteLte({
      lte: endOfDay(addDays(targetDay, -1))
    });
  }

  protected static getAfterDay(targetDay: Date): {
    gte: DateType;
  } {
    return UniversalFilterDateUtils.stringifyGteLte({
      gte: startOfDay(addDays(targetDay, 1))
    });
  }

  protected static getDaysRange([dayA, dayB]: [Date, Date]): {
    gte: DateType;
    lte: DateType;
  } {
    if (dayA < dayB) {
      return UniversalFilterDateUtils.stringifyGteLte({
        gte: startOfDay(dayA),
        lte: endOfDay(dayB)
      });
    } else {
      return UniversalFilterDateUtils.stringifyGteLte({
        gte: startOfDay(dayB),
        lte: endOfDay(dayA)
      });
    }
  }

  static getDayRangeByDate(targetDate: DateType): {
    gte: DateType;
    lte: DateType;
  } {
    const targetDay = new Date(targetDate);

    return UniversalFilterDateUtils.getDayRange(targetDay);
  }

  static getBeforeByDate(targetDate: DateType): {
    lte: DateType;
  } {
    const targetDay = new Date(targetDate);

    return UniversalFilterDateUtils.getBeforeDay(targetDay);
  }

  static getOnOrBeforeByDate(targetDate: DateType): {
    lte: DateType;
  } {
    const targetDay = addDays(new Date(targetDate), 1);

    return UniversalFilterDateUtils.getBeforeDay(targetDay);
  }

  static getAfterByDate(targetDate: DateType): {
    gte: DateType;
  } {
    const targetDay = new Date(targetDate);

    return UniversalFilterDateUtils.getAfterDay(targetDay);
  }

  static getOnOrAfterByDate(targetDate: DateType): {
    gte: DateType;
  } {
    const targetDay = addDays(new Date(targetDate), -1);

    return UniversalFilterDateUtils.getAfterDay(targetDay);
  }

  static getDayRangeWithDaysOffset(daysOffset = 0): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = daysOffset ? addDays(today, daysOffset) : today;

    return UniversalFilterDateUtils.getDayRange(targetDay);
  }

  static getBeforeWithDaysOffset(daysOffset = 0): {
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = daysOffset ? addDays(today, daysOffset) : today;

    return UniversalFilterDateUtils.getBeforeDay(targetDay);
  }

  static getOnOrBeforeWithDaysOffset(daysOffset = 0): {
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = addDays(today, sum([daysOffset, 1]));

    return UniversalFilterDateUtils.getBeforeDay(targetDay);
  }

  static getAfterWithDaysOffset(daysOffset = 0): {
    gte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = daysOffset ? addDays(today, daysOffset) : today;

    return UniversalFilterDateUtils.getAfterDay(targetDay);
  }

  static getOnOrAfterWithDaysOffset(daysOffset = 0): {
    gte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = addDays(today, sum([daysOffset, -1]));

    return UniversalFilterDateUtils.getAfterDay(targetDay);
  }

  static getDayRangeWithMonthsOffset(monthsOffset: number): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = monthsOffset ? addMonths(today, monthsOffset) : today;

    return UniversalFilterDateUtils.getDayRange(targetDay);
  }

  static getBeforeWithMonthsOffset(monthsOffset: number): {
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = monthsOffset ? addMonths(today, monthsOffset) : today;

    return UniversalFilterDateUtils.getBeforeDay(targetDay);
  }

  static getOnOrBeforeWithMonthsOffset(monthsOffset: number): {
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = monthsOffset
      ? addDays(addMonths(today, monthsOffset), 1)
      : today;

    return UniversalFilterDateUtils.getBeforeDay(targetDay);
  }

  static getAfterWithMonthsOffset(monthsOffset: number): {
    gte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = monthsOffset ? addMonths(today, monthsOffset) : today;

    return UniversalFilterDateUtils.getAfterDay(targetDay);
  }

  static getOnOrAfterWithMonthsOffset(monthsOffset: number): {
    gte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = monthsOffset
      ? addDays(addMonths(today, monthsOffset), -1)
      : today;

    return UniversalFilterDateUtils.getAfterDay(targetDay);
  }

  static getRangeFromTodayByDaysOffset(daysOffset): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = daysOffset ? addDays(today, daysOffset) : today;

    return UniversalFilterDateUtils.getDaysRange([today, targetDay]);
  }

  static getRangeFromTodayByMonthsOffset(monthsOffset: number): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = monthsOffset
      ? addDays(addMonths(today, monthsOffset), monthsOffset > 0 ? -1 : 1)
      : today;

    return UniversalFilterDateUtils.getDaysRange([today, targetDay]);
  }

  static getRangeFromTodayByYearsOffset(yearsOffset: number): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    const targetDay = yearsOffset
      ? addDays(addYears(today, yearsOffset), yearsOffset > 0 ? -1 : 1)
      : today;

    return UniversalFilterDateUtils.getDaysRange([today, targetDay]);
  }

  static getCalendarWeekRange(): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    return UniversalFilterDateUtils.getDaysRange([
      startOfWeek(today),
      endOfWeek(today)
    ]);
  }

  static getCalendarMonthRange(): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    return UniversalFilterDateUtils.getDaysRange([
      startOfMonth(today),
      endOfMonth(today)
    ]);
  }

  static getCalendarYearRange(): {
    gte: DateType;
    lte: DateType;
  } {
    const today = UniversalFilterDateUtils.today();

    return UniversalFilterDateUtils.getDaysRange([
      startOfYear(today),
      endOfYear(today)
    ]);
  }
}

export default UniversalFilterDateUtils;
